import {PasswordInput} from '@mantine/core';
import React, {useState} from 'react';

import {MCP_PRODUCT_NAME} from 'llm/NamingConstants';
import notifications from 'notifications';
import {ControlledAccordion, preventAccordionToggleProps} from 'ui-library/Accordion';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useFormInput from 'ui-library/hooks/useFormInput';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {postJSONPromise} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';

interface AddMCPServerModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

type TransportType = 'STREAMABLE_HTTP' | 'SSE';

function getTransportTypeDisplayName(transportType: TransportType): string {
  return transportType === 'STREAMABLE_HTTP' ? 'Streamable HTTP' : 'SSE';
}

export default function AddMCPServerModal(props: AddMCPServerModalProps): JSX.Element {
  const nameInput = useFormInput('');
  const serverUrlInput = useFormInput('');
  const [transportType, setTransportType] = useState<TransportType>('STREAMABLE_HTTP');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Manual registration state
  const [useManualRegistration, setUseManualRegistration] = useState(false);
  const clientIdInput = useFormInput('');
  const clientSecretInput = useFormInput('');

  // OAuth provider overrides
  const [showOAuthOverrides, setShowOAuthOverrides] = useState(false);
  const [suppressResourceParam, setSuppressResourceParam] = useState(false);
  const authorizationUrlInput = useFormInput('');
  const tokenUrlInput = useFormInput('');

  // Construct the redirect URI from the current origin
  const redirectUri = `${window.location.origin}/1/api/llm/mcp/oauth/callback`;

  const isBaseValid = nameInput.value.trim() !== '' && serverUrlInput.value.trim() !== '';
  const isManualRegistrationValid =
    !useManualRegistration || (clientIdInput.value.trim() !== '' && clientSecretInput.value.trim() !== '');
  // OAuth endpoint URLs must be provided as a pair (both or neither)
  const authUrlTrimmed = authorizationUrlInput.value.trim();
  const tokenUrlTrimmed = tokenUrlInput.value.trim();
  const isOAuthEndpointsValid =
    !useManualRegistration || !showOAuthOverrides || (authUrlTrimmed === '') === (tokenUrlTrimmed === '');
  const isValid = isBaseValid && isManualRegistrationValid && isOAuthEndpointsValid;

  async function handleSubmit(): Promise<void> {
    try {
      setIsSubmitting(true);
      const data: Record<string, unknown> = {
        name: nameInput.value.trim(),
        serverUrl: serverUrlInput.value.trim(),
        transportType,
      };

      if (useManualRegistration) {
        data.clientId = clientIdInput.value.trim();
        data.clientSecret = clientSecretInput.value.trim();

        // Include oauthConfig if overrides section is enabled and any values are set
        if (showOAuthOverrides) {
          const oauthConfig: Record<string, unknown> = {};
          if (suppressResourceParam) {
            oauthConfig.suppressResourceParam = true;
          }
          if (authorizationUrlInput.value.trim() !== '') {
            oauthConfig.authorizationUrl = authorizationUrlInput.value.trim();
          }
          if (tokenUrlInput.value.trim() !== '') {
            oauthConfig.tokenUrl = tokenUrlInput.value.trim();
          }
          if (Object.keys(oauthConfig).length > 0) {
            data.oauthConfig = oauthConfig;
          }
        }
      }

      await withLoadingScreen(
        postJSONPromise({
          url: '/1/api/llm/mcp-server-configs',
          data,
        }),
        `Adding ${MCP_PRODUCT_NAME}...`
      );
      notifications.success(`${MCP_PRODUCT_NAME} added successfully`);
      props.onRequestSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Add ${MCP_PRODUCT_NAME}`}
      onRequestSubmit={handleSubmit}
      onRequestClose={props.onRequestClose}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Add',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isValid || isSubmitting,
        },
      ]}
    >
      <p style={{marginBottom: 16, color: 'var(--text-secondary)'}}>
        Get details from MCP servers that your team hosts or from an external tool you trust to add an{' '}
        {MCP_PRODUCT_NAME}.
      </p>
      <FormGroup>
        <TextField
          label="Name"
          placeholder="My MCP Server"
          {...nameInput}
          autoFocus={true}
          maxLength={255}
          isRequired={true}
        />
      </FormGroup>
      <FormGroup>
        <TextField
          label="Server URL"
          placeholder="https://mcp.example.com/mcp"
          {...serverUrlInput}
          maxLength={2048}
          isRequired={true}
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Transport Type" isRequired={true} />
        <SelectDropdown
          displayName={getTransportTypeDisplayName(transportType)}
          menu={
            <Menu value={transportType}>
              <Option
                value="STREAMABLE_HTTP"
                onSelect={() => {
                  setTransportType('STREAMABLE_HTTP');
                }}
              >
                {getTransportTypeDisplayName('STREAMABLE_HTTP')}
              </Option>
              <Option
                value="SSE"
                onSelect={() => {
                  setTransportType('SSE');
                }}
              >
                {getTransportTypeDisplayName('SSE')}
              </Option>
            </Menu>
          }
        />
      </FormGroup>

      {/* Advanced: Manual Registration */}
      <div style={{marginTop: 16, padding: 16, backgroundColor: GRAY_LIGHT, borderRadius: 4}}>
        <ControlledAccordion
          isOpen={useManualRegistration}
          setIsOpen={setUseManualRegistration}
          headerStyle="borderless"
          headerSpacing="none"
          header={
            <span style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500}}>
              Manual OAuth registration
              <Tooltip text="Use this if the MCP server requires you to manually register an OAuth application in their developer console.">
                <span {...preventAccordionToggleProps()} style={{cursor: 'help', display: 'inline-flex'}}>
                  <BenchlingIcon icon="info-circle" color={GRAY_SEMIDARK} />
                </span>
              </Tooltip>
            </span>
          }
        >
          <div style={{paddingTop: 16}}>
            <FormGroup>
              <Label>Redirect URI (click to copy)</Label>
              <FormInput
                readOnly={true}
                selectAllOnClick={true}
                value={redirectUri}
                onClick={() => {
                  copyToClipboard(redirectUri, {
                    showToastOnSuccess: true,
                    toastText: 'Redirect URI copied to clipboard!',
                  });
                }}
                style={{cursor: 'copy'}}
              />
              <p style={{marginTop: 4, marginBottom: 0, fontSize: 12, color: 'var(--text-secondary)'}}>
                Copy this URI and paste it into the MCP server&apos;s developer console when creating your
                OAuth application.
              </p>
            </FormGroup>
            <FormGroup>
              <TextField
                label="Client ID"
                placeholder="Enter the client ID from the MCP server"
                {...clientIdInput}
                maxLength={2048}
                isRequired={true}
              />
            </FormGroup>
            <FormGroup>
              <Label>Client Secret*</Label>
              <PasswordInput
                placeholder="Enter the client secret from the MCP server"
                value={clientSecretInput.value}
                onChange={clientSecretInput.onChange}
                required={true}
                maxLength={2048}
              />
            </FormGroup>

            {/* OAuth Provider Overrides */}
            <div
              style={{marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-default, #e0e0e0)'}}
            >
              <ControlledAccordion
                isOpen={showOAuthOverrides}
                setIsOpen={setShowOAuthOverrides}
                headerStyle="borderless"
                headerSpacing="none"
                header={
                  <span
                    style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500}}
                  >
                    OAuth provider overrides
                    <Tooltip text="For OAuth providers like Microsoft Entra that require non-standard endpoint URLs or don't support RFC 8707 resource parameters.">
                      <span
                        {...preventAccordionToggleProps()}
                        style={{cursor: 'help', display: 'inline-flex'}}
                      >
                        <BenchlingIcon icon="info-circle" color={GRAY_SEMIDARK} />
                      </span>
                    </Tooltip>
                  </span>
                }
              >
                <div style={{paddingTop: 12}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12}}>
                    <CheckboxInput
                      id="suppress-resource-param-checkbox"
                      checked={suppressResourceParam}
                      onChange={(e: InputChangeEvent) => {
                        setSuppressResourceParam(e.target.checked);
                      }}
                      label="Suppress resource parameter"
                    />
                    <Tooltip text="Enable this if the OAuth provider rejects requests containing the RFC 8707 resource parameter.">
                      <span style={{cursor: 'help', display: 'inline-flex'}}>
                        <BenchlingIcon icon="info-circle" color={GRAY_SEMIDARK} />
                      </span>
                    </Tooltip>
                  </div>
                  <FormGroup>
                    <TextField
                      label="Authorization URL"
                      placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/authorize"
                      {...authorizationUrlInput}
                      maxLength={2048}
                    />
                  </FormGroup>
                  <FormGroup>
                    <TextField
                      label="Token URL"
                      placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token"
                      {...tokenUrlInput}
                      maxLength={2048}
                    />
                  </FormGroup>
                  {(authUrlTrimmed === '') !== (tokenUrlTrimmed === '') && (
                    <p
                      style={{
                        marginTop: 0,
                        marginBottom: 0,
                        fontSize: 12,
                        color: 'var(--text-danger, #d32f2f)',
                      }}
                    >
                      Both Authorization URL and Token URL must be provided together.
                    </p>
                  )}
                </div>
              </ControlledAccordion>
            </div>
          </div>
        </ControlledAccordion>
      </div>
    </Modal>
  );
}
